/* eslint-disable react-refresh/only-export-components */
import { TriangleAlert } from 'lucide-react'
import { useCallback } from 'react'
import { ErrorBoundary, FallbackProps, getErrorMessage } from 'react-error-boundary'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetProps,
  SheetTitle,
} from '@eigtech/ui/components/sheet'

export type WithSheetErrorBoundaryProps = SheetProps

export type WithSheetErrorBoundaryOptions = SheetProps & {
  title: React.ReactNode
  description?: React.ReactNode
}

export function withSheetErrorBoundary<Props extends WithSheetErrorBoundaryProps>(
  Component: React.ComponentType<Props>,
  options: WithSheetErrorBoundaryOptions | ((props: Props) => WithSheetErrorBoundaryOptions)
) {
  return function WithSheetErrorBoundary(props: Props) {
    const FallbackComponent = useCallback(
      (fallbackProps: FallbackProps) => (
        <SheetErrorBoundary
          {...props}
          {...(typeof options === 'function' ? options(props) : options)}
          {...fallbackProps}
        />
      ),
      [props]
    )

    return (
      <ErrorBoundary fallbackRender={FallbackComponent}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}

function SheetErrorBoundary({
  title,
  description,
  error,
  resetErrorBoundary: __resetErrorBoundary,
  ...props
}: WithSheetErrorBoundaryOptions & FallbackProps) {
  return (
    <Sheet {...props}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {!!description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <SheetBody>
          <Alert variant="destructive">
            <AlertIcon>
              <TriangleAlert />
            </AlertIcon>
            <AlertContent>
              <AlertTitle>Whoops! Something went wrong.</AlertTitle>
              <AlertDescription>
                <pre className="whitespace-pre-wrap break-normal font-mono">
                  {(getErrorMessage(error) ?? error) ? String(error) : 'Unknown error'}
                </pre>
              </AlertDescription>
            </AlertContent>
          </Alert>
        </SheetBody>

        <SheetFooter>
          <SheetClose>Close</SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
